import * as pc from "playcanvas";
import { bleedIntoTransparentPixels } from "3d/bleed-transparent-pixels";
var CAMERA_ENTITY_NAME = "Camera";
/**
 * A flat square, face up (+Y), that shows a 2D icon on it, for marking a spot
 * in the scene without a 3D model. It is an ordinary mesh so the picker,
 * hover/selection fades (`material-effects`) and bounding boxes treat it like
 * any model. The square is centred on the entity's origin; place it with
 * `entity.setLocalPosition`.
 */
export var createIconPlane = function (app, _a) {
    var name = _a.name, imageUrl = _a.imageUrl, size = _a.size;
    var material = new pc.StandardMaterial();
    /* Unlit: the icon is drawn through the emissive channel so its colours are
     * exactly the image's, whatever the scene lighting does to the models
     * around it. `emphasizeMaterial` darkens emissive too, so it still dims on
     * hover, and `fadeMaterial` works through the opacity as usual. */
    material.diffuse = new pc.Color(0, 0, 0);
    material.emissive = new pc.Color(1, 1, 1);
    material.emissiveTint = true;
    material.useLighting = false;
    material.useSkybox = false;
    material.opacityMapChannel = "a";
    material.blendType = pc.BLEND_NORMAL;
    material.cull = pc.CULLFACE_NONE;
    material.update();
    var texture;
    var disposed = false;
    var image = new Image();
    image.onload = function () {
        if (disposed)
            return;
        var width = image.width, height = image.height;
        var canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        var context = canvas.getContext("2d");
        if (!context)
            return;
        context.drawImage(image, 0, 0);
        var data = context.getImageData(0, 0, width, height).data;
        bleedIntoTransparentPixels(data, width, height);
        /* Written into the texture directly: going back through a canvas would
         * premultiply the bled colours away again. */
        texture = new pc.Texture(app.graphicsDevice, {
            width: width,
            height: height,
            format: pc.PIXELFORMAT_R8_G8_B8_A8,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        texture.lock().set(data);
        texture.unlock();
        material.emissiveMap = texture;
        material.opacityMap = texture;
        material.update();
    };
    image.src = imageUrl;
    var entity = new pc.Entity(name);
    entity.addComponent("model", { type: "plane" });
    if (entity.model)
        entity.model.material = material;
    entity.setLocalScale(size, 1, size);
    return {
        entity: entity,
        destroy: function () {
            disposed = true;
            entity.destroy();
            material.destroy();
            texture === null || texture === void 0 ? void 0 : texture.destroy();
        },
    };
};
/**
 * An icon plane (`createIconPlane`) that always faces the camera.
 */
export var createIconBillboard = function (app, options) {
    var _a = createIconPlane(app, options), entity = _a.entity, destroy = _a.destroy;
    /* Screen-aligned: copy the camera's rotation, then tip the plane (whose
     * face is +Y) up so its face points back at the viewer (+Z of the camera).
     * Copying the rotation rather than `lookAt` keeps the icon upright and
     * works with the top-down edit view, where `lookAt`'s up vector degenerates. */
    var faceCamera = function () {
        var camera = app.root.findByName(CAMERA_ENTITY_NAME);
        if (!camera)
            return;
        entity.setRotation(camera.getRotation());
        entity.rotateLocal(90, 0, 0);
    };
    faceCamera();
    app.on("update", faceCamera);
    return {
        entity: entity,
        destroy: function () {
            app.off("update", faceCamera);
            destroy();
        },
    };
};
